import type { MediaRef } from '@quiz/shared';

/**
 * The picture, clip or video attached to a question.
 *
 * Deliberately small in what it offers. Every extra control here is a way for
 * somebody to lose the quiz behind another app while the timer runs.
 */
export function QuestionMedia({
  media,
  url,
  state,
}: {
  media: MediaRef;
  url: string | null;
  state: 'idle' | 'loading' | 'ready' | 'error';
}) {
  // Reserve the space before the file is ready, so the options do not jump down
  // the screen the instant it decodes. On a timed question a layout shift is
  // not cosmetic -- it is a mis-tap.
  const ratio =
    media.width && media.height ? `${media.width} / ${media.height}` : media.kind === 'AUDIO' ? undefined : '16 / 9';

  if (state === 'error') {
    return (
      <div className="mt-4 rounded-xl bg-ink-2 p-4 text-center text-sm text-muted">
        {/* The question still has its prompt and its options. Say what is missing
            and let them answer, rather than implying the question is void. */}
        This {media.kind.toLowerCase()} would not load on your phone. Answer from the question above.
      </div>
    );
  }

  if (state !== 'ready' || !url) {
    return (
      <div
        className="mt-4 flex animate-pulse items-center justify-center rounded-xl bg-ink-2 text-sm text-muted"
        style={{ aspectRatio: ratio, minHeight: media.kind === 'AUDIO' ? '4.5rem' : undefined }}
      >
        Unlocking…
      </div>
    );
  }

  if (media.kind === 'IMAGE') {
    return (
      <img
        src={url}
        alt=""
        className="mt-4 w-full rounded-xl bg-ink-2 object-contain"
        style={{ aspectRatio: ratio }}
      />
    );
  }

  if (media.kind === 'AUDIO') {
    return (
      <audio
        src={url}
        controls
        // No autoplay: iOS refuses it without a gesture, and a page that tries
        // and fails looks broken. The tap is the design, not a workaround.
        className="mt-4 w-full"
      />
    );
  }

  return (
    <video
      src={url}
      controls
      playsInline
      // playsInline and no fullscreen, together. Fullscreen on a phone hands
      // the player a share sheet and a way out of the quiz mid-timer; inline
      // keeps the question and the countdown on screen while it plays.
      disablePictureInPicture
      controlsList="nofullscreen nodownload noremoteplayback"
      className="mt-4 w-full rounded-xl bg-ink-2"
      style={{ aspectRatio: ratio }}
    />
  );
}
